import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useAuth } from "../../lib/auth";
import { api } from "../../lib/api";
import { useIntl } from "../../lib/i18n";
import RichTextBlock from "../../components/RichTextBlock";
import ImagePicker, { UploadResult } from "../../components/ImagePicker";

// Epic 33 — visual CMS editor. Four subsystems:
//   33.1/33.2  section list + drag-drop reorder + save creates a new revision
//   33.3       Inline rich-text edit per block (auto-save debounce → POST)
//   33.4       Preview + Publish + revision history
//   33.6       Theme picker (primary color, fonts, hero variant)
//
// Sections are rendered as draggable cards. Drag-and-drop uses native
// HTML5 dnd (sufficient for ≤10 sections; no library dependency).
// Rich-text body lives on `props.html` for every block; the editor
// auto-saves a new revision 800ms after the last edit blur (publish
// keeps pointing at the previously-published revision until the user
// explicitly clicks Publish).

type SectionBlock = {
  id: string;
  kind: string;
  order: number;
  props?: Record<string, unknown>;
};

// extractHTML pulls the rich-text payload from a block's props bag.
// Falls back to empty so a legacy block (no props) renders the
// placeholder.
function extractHTML(block: SectionBlock): string {
  const html = block.props?.["html"];
  return typeof html === "string" ? html : "";
}

// extractImageURL pulls the 800px display URL from a block's props bag.
// Blocks that support imagery (hero, aboutFirm, testimonials, custom)
// use `props.imageUrl` for the small display URL and `props.imageUrl1600`
// for the retina/large variant.
function extractImageURL(block: SectionBlock): string | null {
  const u = block.props?.["imageUrl"];
  return typeof u === "string" && u.length > 0 ? u : null;
}

type Theme = {
  primaryColor?: string;
  fontHeading?: string;
  fontBody?: string;
  heroVariant?: string;
};

type Revision = {
  id: string;
  sections: SectionBlock[];
  theme: Theme;
  note?: string;
  createdAt: string;
  createdBy?: string;
  isPublished: boolean;
};

const SECTION_KINDS = ["hero", "practiceAreas", "aboutFirm", "testimonials", "contact"];
const HERO_VARIANTS = ["standard", "split", "video", "minimal"];
const FONT_OPTIONS = ["system-ui", "Inter", "Georgia", "Lora", "Source Serif"];
const COLOR_PRESETS = ["#1e3a8a", "#0f766e", "#7c2d12", "#374151", "#6d28d9"];

type PublishedPreview = {
  sections: SectionBlock[];
  theme: Theme;
  publishedAt?: string;
};

export default function SiteBuilderPage() {
  const { session } = useAuth();
  const { t } = useIntl();
  const [sections, setSections] = useState<SectionBlock[]>([]);
  const [theme, setTheme] = useState<Theme>({});
  const [revisions, setRevisions] = useState<Revision[]>([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [publishing, setPublishing] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [dragIndex, setDragIndex] = useState<number | null>(null);
  const [autoSaveStatus, setAutoSaveStatus] = useState<"idle" | "pending" | "saved" | "error">("idle");
  const autoSaveTimer = useRef<number | null>(null);
  const dirtyRef = useRef(false);

  // 33.4 — Preview modal state. `previewMode` controls what's shown:
  //   "draft"     → renders the in-memory working state (what would publish next)
  //   "published" → calls GET /api/v1/tenant/site/preview and renders the
  //                 currently-published revision (or "nothing published yet")
  const [previewOpen, setPreviewOpen] = useState(false);
  const [previewMode, setPreviewMode] = useState<"draft" | "published">("draft");
  const [previewPublished, setPreviewPublished] = useState<PublishedPreview | null>(null);
  const [previewLoading, setPreviewLoading] = useState(false);
  const [previewError, setPreviewError] = useState<string | null>(null);

  const load = useCallback(async () => {
    if (!session?.token) return;
    setLoading(true);
    setError(null);
    try {
      const [siteR, revR] = await Promise.all([
        api("/api/v1/tenant/site", { token: session.token }),
        api("/api/v1/tenant/site/revisions", { token: session.token }),
      ]);
      if (!siteR.ok) throw new Error(await siteR.text());
      if (!revR.ok) throw new Error(await revR.text());
      const siteBody = (await siteR.json()) as { themeConfig?: Theme; sectionOrder?: string[] };
      const revBody = (await revR.json()) as { revisions: Revision[] };
      setRevisions(revBody.revisions ?? []);
      // Hydrate from latest revision if any; otherwise empty.
      const latest = revBody.revisions?.[0];
      if (latest) {
        setSections(latest.sections ?? []);
        setTheme(latest.theme ?? {});
      } else {
        // Backwards-compat — synthesise sections from the legacy
        // sectionOrder list so the first save in the new system
        // doesn't drop the old shape.
        const order = (siteBody.sectionOrder ?? []) as string[];
        setSections(
          order.map((kind, i) => ({
            id: cryptoRandomID(),
            kind,
            order: i,
          })),
        );
        setTheme((siteBody.themeConfig ?? {}) as Theme);
      }
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
    } finally {
      setLoading(false);
    }
  }, [session?.token]);

  useEffect(() => {
    void load();
  }, [load]);

  const sortedSections = useMemo(
    () => [...sections].sort((a, b) => a.order - b.order),
    [sections],
  );

  const moveSection = (from: number, to: number) => {
    if (from === to) return;
    const next = [...sortedSections];
    const [moved] = next.splice(from, 1);
    next.splice(to, 0, moved);
    setSections(next.map((s, i) => ({ ...s, order: i })));
  };

  const addSection = (kind: string) => {
    setSections((prev) => [
      ...prev,
      { id: cryptoRandomID(), kind, order: prev.length },
    ]);
  };

  const removeSection = (id: string) => {
    setSections((prev) =>
      prev.filter((s) => s.id !== id).map((s, i) => ({ ...s, order: i })),
    );
  };

  const saveRevision = async () => {
    setSaving(true);
    setError(null);
    try {
      const res = await api("/api/v1/tenant/site/revisions", {
        method: "POST",
        token: session?.token,
        body: JSON.stringify({ sections: sortedSections, theme }),
      });
      if (!res.ok) throw new Error(await res.text());
      await load();
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
    } finally {
      setSaving(false);
    }
  };

  // 33.3 — auto-save debounce. updateBlockHTML is wired into each
  // RichTextBlock; the debounce timer fires 800ms after the last
  // edit and POSTs a new revision. Publish stays explicit — the
  // user clicks Publish on the row they want public.
  const updateBlockHTML = useCallback((id: string, html: string) => {
    setSections((prev) =>
      prev.map((s) => {
        if (s.id !== id) return s;
        const props = { ...(s.props ?? {}), html };
        return { ...s, props };
      }),
    );
    dirtyRef.current = true;
    setAutoSaveStatus("pending");
  }, []);

  // updateBlockImage stores the 33.5 upload response on the block. The
  // 800px url powers the SPA preview + becomes the public site's <img
  // src>; the 1600px url is retained as an optional retina variant.
  const updateBlockImage = useCallback((id: string, result: UploadResult) => {
    const small = result.variants.find((v) => v.width === 800);
    const large = result.variants.find((v) => v.width === 1600);
    setSections((prev) =>
      prev.map((s) => {
        if (s.id !== id) return s;
        const props = {
          ...(s.props ?? {}),
          imageId: result.imageId,
          imageUrl: small?.url ?? large?.url ?? "",
          imageUrl1600: large?.url ?? "",
        };
        return { ...s, props };
      }),
    );
    dirtyRef.current = true;
    setAutoSaveStatus("pending");
  }, []);

  // autoSave is the inner network call — used by the debounce timer
  // AND by the explicit Save button. Kept off the render cycle to
  // avoid stale-closure pitfalls (reads from refs).
  const sectionsRef = useRef(sortedSections);
  const themeRef = useRef(theme);
  useEffect(() => {
    sectionsRef.current = sortedSections;
  }, [sortedSections]);
  useEffect(() => {
    themeRef.current = theme;
  }, [theme]);

  const autoSave = useCallback(async () => {
    if (!session?.token) return;
    try {
      const res = await api("/api/v1/tenant/site/revisions", {
        method: "POST",
        token: session.token,
        body: JSON.stringify({
          sections: sectionsRef.current,
          theme: themeRef.current,
          note: "autosave",
        }),
      });
      if (!res.ok) {
        setAutoSaveStatus("error");
        setError(await res.text());
        return;
      }
      dirtyRef.current = false;
      setAutoSaveStatus("saved");
      // Refresh revision list so the user sees the new row land.
      const revR = await api("/api/v1/tenant/site/revisions", { token: session.token });
      if (revR.ok) {
        const body = (await revR.json()) as { revisions: Revision[] };
        setRevisions(body.revisions ?? []);
      }
    } catch (e) {
      setAutoSaveStatus("error");
      setError(e instanceof Error ? e.message : String(e));
    }
  }, [session?.token]);

  // Debounce — every state change that flips dirtyRef.current arms a
  // fresh 800ms timer; cleared on unmount.
  useEffect(() => {
    if (!dirtyRef.current) return;
    if (autoSaveTimer.current !== null) {
      window.clearTimeout(autoSaveTimer.current);
    }
    autoSaveTimer.current = window.setTimeout(() => {
      void autoSave();
    }, 800);
    return () => {
      if (autoSaveTimer.current !== null) {
        window.clearTimeout(autoSaveTimer.current);
        autoSaveTimer.current = null;
      }
    };
  }, [sections, theme, autoSave]);

  // openPreview opens the modal in "draft" mode (no network) — and, on
  // demand, the modal can also fetch the published revision via
  // GET /api/v1/tenant/site/preview to compare. We pre-fetch the
  // published shape on first open so the toggle is instant.
  const openPreview = useCallback(async () => {
    setPreviewMode("draft");
    setPreviewError(null);
    setPreviewOpen(true);
    if (previewPublished !== null || !session?.token) return;
    setPreviewLoading(true);
    try {
      const res = await api("/api/v1/tenant/site/preview", { token: session.token });
      if (res.status === 204) {
        setPreviewPublished({ sections: [], theme: {} });
      } else if (res.ok) {
        const body = (await res.json()) as PublishedPreview;
        setPreviewPublished({
          sections: body.sections ?? [],
          theme: body.theme ?? {},
          publishedAt: body.publishedAt,
        });
      } else {
        setPreviewError(await res.text());
      }
    } catch (e) {
      setPreviewError(e instanceof Error ? e.message : String(e));
    } finally {
      setPreviewLoading(false);
    }
  }, [previewPublished, session?.token]);

  const closePreview = () => setPreviewOpen(false);

  const publish = async (revisionId: string) => {
    setPublishing(revisionId);
    setError(null);
    try {
      const res = await api("/api/v1/tenant/site/publish", {
        method: "POST",
        token: session?.token,
        body: JSON.stringify({ revisionId }),
      });
      if (!res.ok) throw new Error(await res.text());
      await load();
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
    } finally {
      setPublishing(null);
    }
  };

  return (
    <div>
      <h1 className="text-2xl font-semibold text-brand-900">{t("site.builder.title")}</h1>
      <p className="mt-2 text-sm text-slate-500">{t("site.builder.subtitle")}</p>
      {loading && <p className="mt-4 text-slate-500">…</p>}
      {error && <p className="mt-4 text-rose-600">{error}</p>}

      {!loading && (
        <div className="mt-6 grid gap-6 md:grid-cols-[2fr_1fr]">
          <section className="rounded-md border border-slate-200 bg-white p-4">
            <header className="mb-3 flex items-center justify-between">
              <h2 className="text-sm font-semibold text-brand-900">{t("site.builder.sections")}</h2>
              <div className="flex gap-2">
                {SECTION_KINDS.map((k) => (
                  <button
                    key={k}
                    type="button"
                    onClick={() => addSection(k)}
                    className="rounded-md border border-slate-300 px-2 py-1 text-xs hover:bg-slate-50"
                  >
                    + {k}
                  </button>
                ))}
              </div>
            </header>
            <ul className="space-y-2">
              {sortedSections.length === 0 && (
                <li className="rounded border border-dashed border-slate-300 p-3 text-sm text-slate-500">
                  {t("site.builder.empty")}
                </li>
              )}
              {sortedSections.map((s, i) => (
                <li
                  key={s.id}
                  draggable
                  onDragStart={() => setDragIndex(i)}
                  onDragOver={(e) => e.preventDefault()}
                  onDrop={() => {
                    if (dragIndex !== null) moveSection(dragIndex, i);
                    setDragIndex(null);
                  }}
                  className="rounded border border-slate-200 bg-slate-50 p-3 text-sm"
                  data-testid={`site-builder-section-${s.kind}`}
                >
                  <div className="flex items-center justify-between">
                    <span className="flex items-center gap-2">
                      <span className="cursor-grab text-slate-400">⋮⋮</span>
                      <span className="font-mono text-xs text-slate-500">#{i + 1}</span>
                      <span className="font-medium">{s.kind}</span>
                    </span>
                    <button
                      type="button"
                      onClick={() => removeSection(s.id)}
                      className="text-xs text-rose-600 hover:underline"
                    >
                      {t("site.builder.remove")}
                    </button>
                  </div>
                  <div className="mt-3">
                    <RichTextBlock
                      // key forces a fresh editor on id swap (e.g. drag-drop reorders the dom).
                      key={s.id}
                      initialHTML={extractHTML(s)}
                      placeholder={t("site.builder.block.placeholder") + " (" + s.kind + ")"}
                      ariaLabel={t("site.builder.block.aria") + " — " + s.kind}
                      onChange={(html) => updateBlockHTML(s.id, html)}
                      disabled={saving}
                    />
                  </div>
                  <div className="mt-2">
                    <ImagePicker
                      onSelect={(result) => updateBlockImage(s.id, result)}
                      currentUrl={extractImageURL(s)}
                      disabled={saving}
                      ariaLabel={t("image_picker.upload") + " — " + s.kind}
                    />
                  </div>
                </li>
              ))}
            </ul>
            <div className="mt-4 flex items-center gap-2">
              <button
                type="button"
                onClick={saveRevision}
                disabled={saving}
                className="rounded-md bg-brand-700 px-4 py-2 text-sm text-white hover:bg-brand-500 disabled:opacity-50"
              >
                {saving ? t("common.saving") : t("site.builder.save")}
              </button>
              <button
                type="button"
                onClick={() => void openPreview()}
                className="rounded-md border border-brand-700 px-4 py-2 text-sm text-brand-700 hover:bg-brand-50"
                data-testid="site-builder-preview"
              >
                {t("site.builder.preview")}
              </button>
              <span
                className="text-xs text-slate-500"
                data-testid="site-builder-autosave"
                aria-live="polite"
              >
                {autoSaveStatus === "pending" && t("site.builder.autosave.pending")}
                {autoSaveStatus === "saved" && t("site.builder.autosave.saved")}
                {autoSaveStatus === "error" && (
                  <span className="text-rose-600">{t("site.builder.autosave.error")}</span>
                )}
              </span>
            </div>
          </section>

          <section className="rounded-md border border-slate-200 bg-white p-4">
            <h2 className="text-sm font-semibold text-brand-900">{t("site.builder.theme")}</h2>

            <label className="mt-3 block text-xs font-medium text-slate-700">
              {t("site.builder.primary_color")}
            </label>
            <div className="mt-1 flex flex-wrap gap-2">
              {COLOR_PRESETS.map((c) => (
                <button
                  key={c}
                  type="button"
                  onClick={() => setTheme((p) => ({ ...p, primaryColor: c }))}
                  className={`h-8 w-8 rounded-full ring-2 ${
                    theme.primaryColor === c ? "ring-brand-900" : "ring-transparent"
                  }`}
                  style={{ background: c }}
                  aria-label={c}
                />
              ))}
              <input
                type="color"
                value={theme.primaryColor ?? "#1e3a8a"}
                onChange={(e) => setTheme((p) => ({ ...p, primaryColor: e.target.value }))}
                className="h-8 w-12 cursor-pointer rounded border border-slate-300"
              />
            </div>

            <label className="mt-4 block text-xs font-medium text-slate-700">
              {t("site.builder.font_heading")}
            </label>
            <select
              value={theme.fontHeading ?? FONT_OPTIONS[0]}
              onChange={(e) => setTheme((p) => ({ ...p, fontHeading: e.target.value }))}
              className="mt-1 w-full rounded-md border border-slate-300 px-3 py-2 text-sm"
            >
              {FONT_OPTIONS.map((f) => (
                <option key={f} value={f}>
                  {f}
                </option>
              ))}
            </select>

            <label className="mt-4 block text-xs font-medium text-slate-700">
              {t("site.builder.font_body")}
            </label>
            <select
              value={theme.fontBody ?? FONT_OPTIONS[0]}
              onChange={(e) => setTheme((p) => ({ ...p, fontBody: e.target.value }))}
              className="mt-1 w-full rounded-md border border-slate-300 px-3 py-2 text-sm"
            >
              {FONT_OPTIONS.map((f) => (
                <option key={f} value={f}>
                  {f}
                </option>
              ))}
            </select>

            <label className="mt-4 block text-xs font-medium text-slate-700">
              {t("site.builder.hero_variant")}
            </label>
            <select
              value={theme.heroVariant ?? HERO_VARIANTS[0]}
              onChange={(e) => setTheme((p) => ({ ...p, heroVariant: e.target.value }))}
              className="mt-1 w-full rounded-md border border-slate-300 px-3 py-2 text-sm"
            >
              {HERO_VARIANTS.map((h) => (
                <option key={h} value={h}>
                  {h}
                </option>
              ))}
            </select>

            <p className="mt-4 text-xs text-slate-500">{t("site.builder.theme.help")}</p>
          </section>
        </div>
      )}

      {!loading && (
        <section className="mt-8 rounded-md border border-slate-200 bg-white p-4">
          <h2 className="text-sm font-semibold text-brand-900">{t("site.builder.revisions")}</h2>
          <p className="mt-1 text-xs text-slate-500">{t("site.builder.revisions.help")}</p>
          <table className="mt-3 min-w-full text-xs">
            <thead className="bg-slate-50 text-slate-700">
              <tr>
                <th className="px-3 py-2 text-left">{t("site.builder.when")}</th>
                <th className="px-3 py-2 text-left">{t("site.builder.who")}</th>
                <th className="px-3 py-2 text-left">{t("site.builder.sections")}</th>
                <th className="px-3 py-2 text-left">{t("site.builder.status")}</th>
                <th className="px-3 py-2 text-left">{t("site.builder.action")}</th>
              </tr>
            </thead>
            <tbody>
              {revisions.length === 0 && (
                <tr>
                  <td colSpan={5} className="px-3 py-3 text-slate-500">
                    {t("site.builder.no_revisions")}
                  </td>
                </tr>
              )}
              {revisions.map((r) => (
                <tr key={r.id} className="border-t border-slate-100">
                  <td className="px-3 py-2 font-mono">{new Date(r.createdAt).toLocaleString()}</td>
                  <td className="px-3 py-2 font-mono">{r.createdBy?.slice(0, 8) ?? "—"}</td>
                  <td className="px-3 py-2">{r.sections?.length ?? 0}</td>
                  <td className="px-3 py-2">
                    {r.isPublished ? (
                      <span className="rounded bg-emerald-100 px-2 py-0.5 text-emerald-800">
                        {t("site.builder.published")}
                      </span>
                    ) : (
                      <span className="rounded bg-slate-100 px-2 py-0.5 text-slate-700">
                        {t("site.builder.draft")}
                      </span>
                    )}
                  </td>
                  <td className="px-3 py-2">
                    {!r.isPublished && (
                      <button
                        type="button"
                        onClick={() => publish(r.id)}
                        disabled={publishing === r.id}
                        className="rounded-md bg-brand-700 px-3 py-1 text-xs text-white hover:bg-brand-500 disabled:opacity-50"
                      >
                        {publishing === r.id ? "…" : t("site.builder.publish")}
                      </button>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </section>
      )}

      {previewOpen && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center bg-slate-900/50 px-4"
          role="dialog"
          aria-modal="true"
          aria-labelledby="site-preview-title"
        >
          <div className="max-h-[85vh] w-full max-w-3xl overflow-y-auto rounded-lg bg-white p-6 shadow-xl">
            <header className="flex items-start justify-between">
              <div>
                <h2 id="site-preview-title" className="text-lg font-semibold text-brand-900">
                  {t("site.builder.preview.title")}
                </h2>
                <p className="mt-1 text-xs text-slate-500">
                  {t("site.builder.preview.help")}
                </p>
              </div>
              <button
                type="button"
                onClick={closePreview}
                className="rounded text-sm text-slate-500 hover:text-slate-900"
                aria-label={t("common.close")}
              >
                ✕
              </button>
            </header>

            <div className="mt-4 flex gap-2 border-b border-slate-200">
              <button
                type="button"
                onClick={() => setPreviewMode("draft")}
                className={`-mb-px border-b-2 px-3 py-2 text-xs ${
                  previewMode === "draft"
                    ? "border-brand-700 font-semibold text-brand-900"
                    : "border-transparent text-slate-500 hover:text-slate-900"
                }`}
              >
                {t("site.builder.preview.draft")}
              </button>
              <button
                type="button"
                onClick={() => setPreviewMode("published")}
                className={`-mb-px border-b-2 px-3 py-2 text-xs ${
                  previewMode === "published"
                    ? "border-brand-700 font-semibold text-brand-900"
                    : "border-transparent text-slate-500 hover:text-slate-900"
                }`}
              >
                {t("site.builder.preview.published")}
              </button>
            </div>

            {previewMode === "draft" && (
              <PreviewBody sections={sortedSections} theme={theme} />
            )}
            {previewMode === "published" && (
              <>
                {previewLoading && <p className="mt-4 text-sm text-slate-500">…</p>}
                {previewError && <p className="mt-4 text-sm text-rose-600">{previewError}</p>}
                {!previewLoading && !previewError && previewPublished && (
                  previewPublished.sections.length === 0 ? (
                    <p className="mt-4 text-sm text-slate-500">
                      {t("site.builder.preview.nothing_published")}
                    </p>
                  ) : (
                    <PreviewBody
                      sections={previewPublished.sections}
                      theme={previewPublished.theme}
                      publishedAt={previewPublished.publishedAt}
                    />
                  )
                )}
              </>
            )}
          </div>
        </div>
      )}
    </div>
  );
}

// PreviewBody renders the working-draft or published sections as a
// stacked mockup using the chosen theme. Section "kinds" map to short
// blurb cards rather than full HTML — this is a structural preview,
// not a full WYSIWYG render. The public preview at /preview/<slug>/<locale>
// gives the full browser-rendered view; this modal is the in-place check
// before publish.
function PreviewBody({
  sections,
  theme,
  publishedAt,
}: {
  sections: SectionBlock[];
  theme: Theme;
  publishedAt?: string;
}) {
  const primary = theme.primaryColor ?? "#1e3a8a";
  return (
    <div className="mt-4 space-y-3">
      {publishedAt && (
        <p className="text-xs text-slate-500">
          Published {new Date(publishedAt).toLocaleString()}
        </p>
      )}
      {sections.length === 0 && (
        <p className="rounded border border-dashed border-slate-300 p-4 text-sm text-slate-500">
          (empty — no sections to render)
        </p>
      )}
      {sections.map((s, i) => {
        const html = extractHTML(s);
        const imageUrl = extractImageURL(s);
        return (
          <div
            key={s.id || `${s.kind}-${i}`}
            className="rounded-md border border-slate-200 p-4 text-sm"
            style={{
              borderLeftColor: primary,
              borderLeftWidth: 4,
              fontFamily: theme.fontBody ?? "system-ui",
            }}
          >
            <div
              className="text-xs font-semibold uppercase tracking-wide"
              style={{ color: primary, fontFamily: theme.fontHeading ?? "system-ui" }}
            >
              #{i + 1} · {s.kind}
              {s.kind === "hero" && theme.heroVariant && (
                <span className="ml-2 font-normal lowercase text-slate-500">
                  ({theme.heroVariant})
                </span>
              )}
            </div>
            {imageUrl && (
              <img
                src={imageUrl}
                alt=""
                className="mt-2 max-h-40 w-full rounded object-cover"
              />
            )}
            {html ? (
              <div
                className="prose prose-sm mt-2 max-w-none text-slate-800"
                // Server-side sanitisation lives on the public-site
                // renderer (epic 33 acceptance); the preview here
                // mirrors what the OWNER just typed.
                dangerouslySetInnerHTML={{ __html: html }}
              />
            ) : (
              <div className="mt-1 text-slate-700">{previewBlurb(s.kind)}</div>
            )}
          </div>
        );
      })}
    </div>
  );
}

function previewBlurb(kind: string): string {
  switch (kind) {
    case "hero":
      return "Headline + sub-headline + CTA";
    case "practiceAreas":
      return "Cards for each practice area (immigration, family, …)";
    case "aboutFirm":
      return "Firm story, attorney bios, credentials";
    case "testimonials":
      return "Client quotes carousel";
    case "contact":
      return "Address, phone, hours, booking link";
    default:
      return `(custom section — ${kind})`;
  }
}

// cryptoRandomID is enough randomness for a client-side UI key. The
// server assigns canonical UUIDs on save.
function cryptoRandomID(): string {
  if (typeof crypto !== "undefined" && typeof crypto.randomUUID === "function") {
    return crypto.randomUUID();
  }
  return Math.random().toString(36).slice(2);
}
